import { AddSocialHistoryDialog } from '@/components/molecules/AddSocialHistoryDialog';
import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useInfiniteRecordList } from '@liza/ui/hooks/useInfiniteRecordList';
import { useTRPC, useTRPCClient } from '@/utils/trpc';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import {
  defaultFilters,
  RecordsListControls,
  type RecordsListFilters,
} from '@liza/ui/components/records/RecordsListControls';
import { SocialHistoryList } from '@liza/ui/components/records/SocialHistoryList';
import { useRecordPromotionStatus } from '@liza/ui/hooks/useRecordPromotionStatus';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  patient: Patient;
};

export const SocialHistory = ({ patient }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const listOptions = {
    ownership: filters.ownership,
    searchParams: { 'code:text': filters.searchQuery },
    status: filters.status,
  };

  const {
    items: observations,
    query,
    bottomSentinelRef,
  } = useInfiniteRecordList({
    queryKey: trpc.patient.observation.list.queryKey({
      medplumPatientId: patient.medplumId,
      category: 'social-history',
      options: listOptions,
    }),
    queryFn: async ({ offset, count }) => {
      const res = await trpcClient.patient.observation.list.query({
        medplumPatientId: patient.medplumId,
        category: 'social-history',
        options: listOptions,
        offset,
        count,
      });
      return { items: res.observations, hasMore: res.hasMore };
    },
  });

  const { mutateAsync: createObservation } = useMutation(
    trpc.patient.observation.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.observation.list.queryKey({
            medplumPatientId: patient.medplumId,
            category: 'social-history',
          }),
        });
      },
    }),
  );

  const { promote, getPromotionStatus } = useRecordPromotionStatus();

  const handlePromotionClick = useCallback(
    (id: string): void => {
      const source = observations.find((o) => o.id === id);
      if (!source) return;

      void promote(id, async () =>
        createObservation({
          resource: {
            ...source,
            id: undefined,
            subject: { reference: `Patient/${patient.medplumId}` },
            effectiveDateTime: new Date().toISOString(),
            issued: new Date().toISOString(),
          },
        }),
      );
    },
    [createObservation, observations, patient, promote],
  );

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('socialHistory')}
        searchPlaceholder={t('searchSocialHistory')}
        onFiltersChange={handleFiltersChange}
        onAdd={() => setIsAddOpen(true)}
        addLabel={t('addSocialHistory')}
      />
      <AddSocialHistoryDialog medplumPatientId={patient.medplumId} isOpen={isAddOpen} onOpenChange={setIsAddOpen} />
      <RecordListScroll
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isFetchingNextPage={query.isFetchingNextPage}
        bottomSentinelRef={bottomSentinelRef}
      >
        <SocialHistoryList
          observations={observations}
          onPromotionClick={filters.ownership === 'external' ? handlePromotionClick : undefined}
          getPromotionStatus={getPromotionStatus}
        />
      </RecordListScroll>
    </div>
  );
};
